// The lines a page draws beside the companies it names (pipeline RC6; module 10 U353, U356).
//
// A page asks for one company's line where it draws it (`useCompanyLine`), and this file gathers every ask made in the
// same moment into one call to GET /api/company-lines, at most MOST_LINES ids a call, so a week of meetings or a list
// of cards costs one request and not one per row. A line read is kept for a few minutes, so moving between pages does
// not read it again. A line that does not come back (a company the person may not read, or a failed call) is simply
// not drawn: the name the page printed stays a link to the record either way.
import { viewFetch as fetch } from '../view-as.js';
import { useEffect, useMemo, useState } from 'react';
import { readerZone, type CompanyLineView } from './api.js';

export const MOST_LINES = 60;
/** How long a line is kept before a page asks for it again. */
const KEEP_MS = 5 * 60_000;
/** How long a failed ask is remembered, so a list does not ask again on every redraw. */
const KEEP_FAILED_MS = 30_000;
/** How long asks wait for others made in the same moment before they go as one call. */
const GATHER_MS = 25;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Kept { at: number; line: CompanyLineView | null; failed: boolean }
type Waiter = (line: CompanyLineView | null) => void;

const kept = new Map<string, Kept>();
const waiting = new Map<string, Map<string, Waiter[]>>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

const keyOf = (store: string, id: string) => `${store}:${id}`;

/** The line already read for this company, while it is fresh; undefined when it has not been read or has gone stale. */
export function keptLine(store: string, id: string, now = Date.now()): CompanyLineView | null | undefined {
  const k = kept.get(keyOf(store, id.toLowerCase()));
  if (!k) return undefined;
  return now - k.at < (k.failed ? KEEP_FAILED_MS : KEEP_MS) ? k.line : undefined;
}

/** Forget every line read (a test, or a change of store). */
export function forgetLines(): void {
  kept.clear();
  waiting.clear();
  for (const t of timers.values()) clearTimeout(t);
  timers.clear();
}

async function send(store: string, asked: Map<string, Waiter[]>): Promise<void> {
  const ids = [...asked.keys()];
  for (let i = 0; i < ids.length; i += MOST_LINES) {
    const chunk = ids.slice(i, i + MOST_LINES);
    let lines: Record<string, CompanyLineView> | null = null;
    try {
      const r = await fetch(`/api/company-lines?store=${encodeURIComponent(store)}&ids=${chunk.join(',')}&zone=${encodeURIComponent(readerZone())}`, { credentials: 'same-origin' });
      if (r.ok) lines = ((await r.json()) as { lines?: Record<string, CompanyLineView> }).lines ?? {};
    } catch {
      lines = null;
    }
    const at = Date.now();
    for (const id of chunk) {
      const line = lines?.[id] ?? null;
      kept.set(keyOf(store, id), { at, line, failed: lines === null });
      for (const w of asked.get(id) ?? []) w(line);
    }
  }
}

/** One company's line, gathered with every other ask made in the same moment into one call. */
export function lineFor(store: string, id: string): Promise<CompanyLineView | null> {
  const lower = id.toLowerCase();
  const have = keptLine(store, lower);
  if (have !== undefined) return Promise.resolve(have);
  return new Promise((resolve) => {
    const forStore = waiting.get(store) ?? new Map<string, Waiter[]>();
    waiting.set(store, forStore);
    forStore.set(lower, [...(forStore.get(lower) ?? []), resolve]);
    if (!timers.has(store)) {
      timers.set(store, setTimeout(() => {
        timers.delete(store);
        const asked = waiting.get(store) ?? new Map<string, Waiter[]>();
        waiting.delete(store);
        void send(store, asked);
      }, GATHER_MS));
    }
  });
}

/** The lines of the companies a page names, by id; a company with no line yet (or none to show) is absent. */
export function useCompanyLines(store: string | null | undefined, ids: ReadonlyArray<string | null | undefined>): Record<string, CompanyLineView> {
  const wanted = useMemo(() => [...new Set(ids.filter((x): x is string => typeof x === 'string' && UUID.test(x)).map((x) => x.toLowerCase()))].sort(), [ids]);
  const key = wanted.join(',');
  const [, redraw] = useState(0);
  useEffect(() => {
    if (!store || wanted.length === 0) return;
    let live = true;
    for (const id of wanted) {
      if (keptLine(store, id) !== undefined) continue;
      void lineFor(store, id).then(() => { if (live) redraw((n) => n + 1); });
    }
    return () => { live = false; };
    // `key` carries `wanted`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, key]);
  const out: Record<string, CompanyLineView> = {};
  if (store) for (const id of wanted) { const l = keptLine(store, id); if (l) out[id] = l; }
  return out;
}

/** One company's line, or null until it is read (or when there is none to show). */
export function useCompanyLine(store: string | null | undefined, id: string | null | undefined): CompanyLineView | null {
  const ids = useMemo(() => [id], [id]);
  const lines = useCompanyLines(store, ids);
  return id ? lines[id.toLowerCase()] ?? null : null;
}
